import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';
import { GraphType } from '@components-clean/central-monitoring/panels/panel-constants';
import { DropDownList } from '@components-clean/core/drop-down-list';
import { StudyDataFilterContextProvider } from '@contexts-clean/study-data-filter-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { DataViewConfigPoC } from '@pages-clean/dummy-page/pages/data-view-config-poc/data-view-config-poc';
import { PanelData } from '@pages-clean/dummy-page/pages/data-view-config-poc/types';
import { PanelGraphType, PanelModelType, PanelTheme } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import { GET_DATA_VIEW_CONFIGURATIONS, GET_PANELS } from '@queries/central-monitoring';
import {
    getPanels,
    getPanelsVariables,
    dataViewConfigurations,
    dataViewConfigurationsVariables,
} from '@queries/generated/central-monitoring.generated';

import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

type DataViewConfig = {
    id: string;
    name: string;
    panelIds: string[];
};

export type FormattedStudyDataSet = {
    formattedDate: string;
} & CM2Dataset;

const PanelGraphTypeToGraphType = (type: PanelGraphType): GraphType => {
    switch (type) {
        case PanelGraphType.COLUMN:
            return 'COLUMN';
        case PanelGraphType.HEATMAP:
            return 'HEATMAP';
        case PanelGraphType.SCATTER_PLOT_LINE:
            return 'SCATTERPLOTLINE';
        case PanelGraphType.STACKED_COLUMN:
            return 'STACKEDCOLUMN';
        case PanelGraphType.DONUT:
        default:
            return 'DONUT';
    }
};

export const sortDatasetByDatesDesc = (a: FormattedStudyDataSet, b: FormattedStudyDataSet) =>
    (b?.dataCutDate ? new Date(b.dataCutDate).getTime() : 0) - (a?.dataCutDate ? new Date(a.dataCutDate).getTime() : 0);

const DataViewConfigPoCPage = () => {
    const { state } = useLocalState();

    // Maybe move away from localState and update the registered route to pass in values
    const orgKey = state?.selectedStudy?.organisation?.organisationKey as string;

    const [selectedDVC, setSelectedDVC] = useState<DataViewConfig | null>(null);
    const [dvConfigs, setDVConfigs] = useState<DataViewConfig[]>([]);
    const [dvcPanels, setDVCPanels] = useState<PanelData[]>([]);
    const [datasets, setDatasets] = useState<FormattedStudyDataSet[]>([]);
    const [selectedDataset, setSelectedDataset] = useState<FormattedStudyDataSet | null>(null);
    const [studyId, setStudyId] = useState<string>(state?.selectedStudy?.id as string);

    const { data: dataViewConfigs } = useQuery<dataViewConfigurations, dataViewConfigurationsVariables>(
        GET_DATA_VIEW_CONFIGURATIONS,
        {
            skip: !studyId,
            variables: { studyId },
        },
    );

    const { data: panelsData } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        skip: !orgKey || !studyId || !selectedDVC,
        variables: {
            organisationId: orgKey,
            studyId,
        },
    });

    const { data: cm2Datasets } = DatasetApi.useGetAll(studyId);

    useEffect(() => {
        const formattedDatasets = cm2Datasets
            ?.map((x) => ({
                ...x,
                formattedDate: moment(x.dataCutDate, 'YYYY-MM-DD').format('DD-MMM-YYYY'),
            }))
            .sort(sortDatasetByDatesDesc);

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasets(formattedDatasets ?? []);
    }, [cm2Datasets]);

    useEffect(() => {
        if (dataViewConfigs?.dataViewConfigurations?.length) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDVConfigs(
                dataViewConfigs.dataViewConfigurations.flatMap<DataViewConfig>((d) => {
                    return d == null
                        ? []
                        : [
                              {
                                  id: d.id as string,
                                  name: d.name || 'No name',
                                  panelIds: (d.panelIds as string[]) || [],
                              },
                          ];
                }),
            );
        } else {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDVConfigs([]);
        }
    }, [dataViewConfigs]);

    useEffect(() => {
        if (panelsData?.panels?.length) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDVCPanels(
                panelsData.panels.flatMap<PanelData>((p) => {
                    if (
                        !p ||
                        (selectedDVC?.panelIds || []).indexOf(p.id as string) < 0 ||
                        p.panelType == null ||
                        p.graph == null ||
                        ['DONUT', 'COLUMN', 'HEATMAP', 'SCATTER_PLOT_LINE', 'STACKED_COLUMN'].indexOf(
                            p.graph.type as PanelGraphType,
                        ) < 0 ||
                        ['TRI_OPERATIONAL'].indexOf(p.modelParams?.type as PanelModelType) < 0
                    )
                        return [];

                    return [
                        {
                            id: p.id as string,
                            studyId: p.studyId as string,
                            name: p.name || 'Default name',
                            panelType: p.panelType,
                            modelType: p.modelParams?.type ?? PanelModelType.FUNNEL,
                            modelParams: p.modelParams ?? null,
                            plotTitle: p.graph?.plotTitle,
                            type: p.graph.type ? PanelGraphTypeToGraphType(p.graph.type) : PanelGraphType.DONUT,
                            xTitle: p.graph.xTitle,
                            yTitle: p.graph.yTitle,
                            theme: p.panelTheme ?? PanelTheme.NONE,
                        },
                    ];
                }),
            );
        } else {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDVCPanels([]);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panelsData]);

    useEffect(() => {
        if (state?.selectedStudy?.id !== studyId) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setSelectedDataset(null);
            setSelectedDVC(null);
            setStudyId(state?.selectedStudy?.id as string);
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [state?.selectedStudy?.id]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <DropDownList
                label="Data View Config"
                data-testid="dv-config-selector"
                data={dvConfigs}
                value={selectedDVC}
                textField="name"
                onChange={(e) => {
                    setSelectedDVC(e.value as DataViewConfig);
                }}
                style={{ width: '400px' }}
            />

            <DropDownList
                label="Dataset"
                data-testid="legacy-dataset-selector"
                data={datasets}
                value={selectedDataset}
                textField="formattedDate"
                style={{ width: '400px' }}
                onChange={(e) => setSelectedDataset(e.value as FormattedStudyDataSet)}
            />

            {selectedDVC?.id && selectedDataset?.id && (
                <StudyDataFilterContextProvider>
                    <DataViewConfigPoC
                        orgKey={orgKey}
                        studyId={studyId}
                        panelData={dvcPanels}
                        dataViewConfigId={selectedDVC.id}
                        dataset={selectedDataset}
                    />
                </StudyDataFilterContextProvider>
            )}
        </div>
    );
};

export default DataViewConfigPoCPage;
